/**
 * Client CSV Import Modal
 * Pick or paste a CSV → preview with duplicate/validation checks (server dry run) → import.
 */

import { useRef, useState } from 'react';
import { api } from '../../services/api';
import { parseCsv, downloadTextFile } from '../../utils/csv';
import {
  CLIENT_IMPORT_COLUMNS,
  CLIENT_IMPORT_MAX_ROWS,
  buildClientImportTemplate,
  mapClientImportHeaders,
  clientImportRowsFromTable,
} from '../../../shared/types/clientImport';
import type { ClientImportRow, ClientImportResponse } from '../../../shared/types/clientImport';

interface Props {
  onClose: () => void;
  onImported: (created: number) => void;
}

type Step = 'input' | 'preview' | 'done';

export function ClientImportModal({ onClose, onImported }: Props) {
  const [step, setStep] = useState<Step>('input');
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ClientImportRow[]>([]);
  const [unknownHeaders, setUnknownHeaders] = useState<string[]>([]);
  const [dryRun, setDryRun] = useState<ClientImportResponse | null>(null);
  const [result, setResult] = useState<ClientImportResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showColumns, setShowColumns] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const isMobile = window.innerWidth < 768;

  const handleFile = async (file: File) => {
    setFileName(file.name);
    setCsvText(await file.text());
  };

  const preview = async () => {
    setError('');
    const text = csvText.trim();
    if (!text) { setError('Choose a CSV file or paste CSV text first.'); return; }
    const parsed = parseCsv(text);
    if (parsed.headers.length === 0) { setError('The CSV has no header row.'); return; }
    const { mapping, unknown, hasName } = mapClientImportHeaders(parsed.headers);
    if (!hasName) { setError('A "name" column is required (aliases: client, organization, company).'); return; }
    const importRows = clientImportRowsFromTable(mapping, parsed.records);
    if (importRows.length === 0) { setError('No data rows found under the header.'); return; }
    if (importRows.length > CLIENT_IMPORT_MAX_ROWS) { setError(`Too many rows (${importRows.length}). The limit is ${CLIENT_IMPORT_MAX_ROWS} per import.`); return; }

    setBusy(true);
    try {
      const res = await api.admin.importClients(importRows, true);
      setRows(importRows);
      setUnknownHeaders(unknown);
      setDryRun(res);
      setStep('preview');
    } catch (err: any) {
      setError(err.message || 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    if (!dryRun || dryRun.failed > 0) return;
    setBusy(true);
    setError('');
    try {
      const res = await api.admin.importClients(rows, false);
      setResult(res);
      if (res.failed > 0) {
        setDryRun(res);
        setError(`${res.failed} row(s) were rejected on the server (likely a client created in the meantime). Nothing was imported.`);
      } else {
        setStep('done');
        onImported(res.created);
      }
    } catch (err: any) {
      setError(err.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const errorCount = dryRun?.failed ?? 0;
  const warningCount = dryRun ? dryRun.results.filter(r => r.warnings.length > 0).length : 0;

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, ...(step === 'preview' ? { width: 1000 } : {}) }} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <h2 style={s.title}>
            {step === 'input' && 'Import Clients from CSV'}
            {step === 'preview' && `Preview — ${rows.length} client${rows.length === 1 ? '' : 's'}`}
            {step === 'done' && 'Import Complete'}
          </h2>
          <button onClick={onClose} style={s.closeBtn}>&times;</button>
        </div>

        <div style={s.body}>
          {step === 'input' && (
            <>
              <p style={s.help}>
                One row per organization. Only <code style={s.code}>name</code> is required; org codes are generated when blank.
                Every row is checked against existing clients: a matching name (ignoring case and punctuation) or org code is rejected,
                and near-matches such as &ldquo;Acme&rdquo; vs &ldquo;Acme LLC&rdquo; are flagged for you to confirm. Nothing is imported until every row passes.
              </p>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                <button onClick={() => downloadTextFile('coreskan-client-import-template.csv', buildClientImportTemplate())} style={s.secondaryBtn}>
                  Download template
                </button>
                <button onClick={() => setShowColumns(v => !v)} style={s.secondaryBtn}>
                  {showColumns ? 'Hide columns' : 'Show accepted columns'}
                </button>
              </div>

              {showColumns && (
                <div style={s.columnsBox}>
                  {CLIENT_IMPORT_COLUMNS.map(c => (
                    <div key={c.key} style={s.columnRow}>
                      <code style={{ ...s.code, minWidth: 130 }}>{c.header}</code>
                      <span style={{ color: '#94a3b8', fontSize: 12 }}>{c.description}</span>
                    </div>
                  ))}
                </div>
              )}

              <div
                style={s.dropzone}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
                onClick={() => fileRef.current?.click()}
              >
                <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }} />
                <div style={{ color: '#f1f5f9', fontSize: 14, fontWeight: 500 }}>{fileName || 'Click to choose a CSV file, or drop it here'}</div>
                <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>Exported from Excel or Google Sheets as CSV</div>
              </div>

              <div style={{ color: '#64748b', fontSize: 12, margin: '12px 0 6px' }}>…or paste CSV text</div>
              <textarea
                value={csvText}
                onChange={(e) => { setCsvText(e.target.value); if (fileName) setFileName(''); }}
                placeholder={'name,contact_name,contact_email\nAutoZone,Jane Doe,jane@autozone.com'}
                style={s.textarea}
                spellCheck={false}
              />

              {error && <div style={s.error}>{error}</div>}

              <div style={s.actions}>
                <button onClick={onClose} style={s.cancelBtn}>Cancel</button>
                <button onClick={preview} style={{ ...s.primaryBtn, opacity: busy ? 0.6 : 1 }} disabled={busy}>
                  {busy ? 'Checking…' : 'Preview'}
                </button>
              </div>
            </>
          )}

          {step === 'preview' && dryRun && (
            <>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <span style={s.pill('#00cc88')}>{rows.length - errorCount} ready</span>
                {errorCount > 0 && <span style={s.pill('#ef4444')}>{errorCount} rejected</span>}
                {warningCount > 0 && <span style={s.pill('#f59e0b')}>{warningCount} to double-check</span>}
                {unknownHeaders.length > 0 && <span style={{ color: '#f59e0b', fontSize: 12 }}>Ignored columns: {unknownHeaders.join(', ')}</span>}
              </div>

              {errorCount > 0 && (
                <div style={s.error}>
                  Rows marked in red are duplicates or invalid. Remove or fix them in your spreadsheet and preview again. Nothing is imported until every row passes.
                </div>
              )}

              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>#</th>
                      <th style={s.th}>Client</th>
                      <th style={s.th}>Org code</th>
                      <th style={s.th}>Contact</th>
                      <th style={s.th}>Billing</th>
                      <th style={s.th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dryRun.results.map((r, i) => {
                      const raw = rows[i] || {};
                      const tone = !r.ok ? '#ef4444' : r.warnings.length ? '#f59e0b' : '#00cc88';
                      const billing = [raw.billing_city, raw.billing_state].filter(Boolean).join(', ');
                      return (
                        <tr key={r.rowNumber} style={{ background: !r.ok ? 'rgba(239,68,68,0.08)' : undefined }}>
                          <td style={s.td}>{r.rowNumber}</td>
                          <td style={{ ...s.td, color: '#f1f5f9', fontWeight: 500 }}>{r.name || <span style={{ color: '#475569' }}>(blank)</span>}</td>
                          <td style={{ ...s.td, fontFamily: 'monospace' }}>{r.orgCode || <span style={{ color: '#475569' }}>auto</span>}</td>
                          <td style={s.td}>{[raw.contact_name, raw.contact_email].filter(Boolean).join(' · ') || <span style={{ color: '#475569' }}>—</span>}</td>
                          <td style={s.td}>{billing || <span style={{ color: '#475569' }}>—</span>}</td>
                          <td style={{ ...s.td, color: tone, maxWidth: 360 }}>
                            {r.ok && r.warnings.length === 0 && 'Ready'}
                            {r.errors.map((e, k) => <div key={`e${k}`}>{e}</div>)}
                            {r.warnings.map((w, k) => <div key={`w${k}`} style={{ color: '#f59e0b' }}>{w}</div>)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {error && <div style={s.error}>{error}</div>}

              <div style={{ ...s.actions, flexDirection: isMobile ? 'column-reverse' : 'row' }}>
                <button onClick={() => { setStep('input'); setError(''); }} style={s.cancelBtn} disabled={busy}>Back</button>
                <button onClick={doImport} style={{ ...s.primaryBtn, opacity: errorCount > 0 || busy ? 0.4 : 1 }} disabled={errorCount > 0 || busy}>
                  {busy ? 'Importing…' : `Import ${rows.length} client${rows.length === 1 ? '' : 's'}`}
                </button>
              </div>
            </>
          )}

          {step === 'done' && result && (
            <>
              <div style={{ color: '#00cc88', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                {result.created} client{result.created === 1 ? '' : 's'} created
              </div>
              <p style={s.help}>Org codes were assigned automatically where the file left them blank. Open a client to upload its logo and add members.</p>
              <div style={s.actions}>
                <button onClick={onClose} style={s.primaryBtn}>Close</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 } as React.CSSProperties,
  modal: { background: '#1a1a1a', border: '1px solid #333333', borderRadius: 16, width: 640, maxWidth: '96vw', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.5)' } as React.CSSProperties,
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #333333' } as React.CSSProperties,
  title: { fontSize: 18, fontWeight: 600, color: '#f1f5f9', margin: 0 } as React.CSSProperties,
  closeBtn: { background: 'none', border: 'none', color: '#64748b', fontSize: 24, cursor: 'pointer', padding: '0 4px', lineHeight: 1 } as React.CSSProperties,
  body: { padding: 24 } as React.CSSProperties,
  help: { color: '#94a3b8', fontSize: 13, lineHeight: 1.5, margin: '0 0 16px' } as React.CSSProperties,
  code: { background: '#0d0d0d', border: '1px solid #333333', borderRadius: 4, padding: '1px 6px', fontSize: 12, color: '#4F7CFF' } as React.CSSProperties,
  columnsBox: { background: '#0d0d0d', border: '1px solid #333333', borderRadius: 8, padding: 12, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 6 } as React.CSSProperties,
  columnRow: { display: 'flex', gap: 12, alignItems: 'flex-start' } as React.CSSProperties,
  dropzone: { border: '1px dashed #475569', borderRadius: 10, padding: 24, textAlign: 'center', cursor: 'pointer', background: '#0d0d0d' } as React.CSSProperties,
  textarea: { width: '100%', minHeight: 120, background: '#0d0d0d', border: '1px solid #333333', borderRadius: 8, padding: '10px 12px', color: '#f1f5f9', fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', outline: 'none', boxSizing: 'border-box', resize: 'vertical' } as React.CSSProperties,
  error: { background: '#7f1d1d', color: '#fca5a5', padding: '10px 14px', borderRadius: 8, fontSize: 13, margin: '12px 0' } as React.CSSProperties,
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 } as React.CSSProperties,
  cancelBtn: { background: 'none', border: '1px solid #475569', borderRadius: 8, color: '#94a3b8', padding: '10px 20px', fontSize: 14, cursor: 'pointer', minHeight: 44 } as React.CSSProperties,
  secondaryBtn: { background: '#333333', color: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer' } as React.CSSProperties,
  primaryBtn: { background: '#4F7CFF', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer', minHeight: 44 } as React.CSSProperties,
  tableWrap: { overflow: 'auto', maxHeight: '55vh', border: '1px solid #333333', borderRadius: 8, background: '#0d0d0d' } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 } as React.CSSProperties,
  th: { textAlign: 'left', padding: '8px 10px', color: '#64748b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #333333', position: 'sticky', top: 0, background: '#0d0d0d', whiteSpace: 'nowrap' } as React.CSSProperties,
  td: { padding: '8px 10px', color: '#94a3b8', borderBottom: '1px solid #1a1a1a', verticalAlign: 'top' } as React.CSSProperties,
  pill: (color: string): React.CSSProperties => ({ color, background: `${color}1a`, border: `1px solid ${color}55`, borderRadius: 10, padding: '2px 10px', fontSize: 12, fontWeight: 600 }),
};
